// Internal Imports
import { logger } from './rollbar.js';

// Local Variables
const loadAttribute = 'data-lazy-load-embed-id';
const rootContainerAttribute = 'data-lazy-load-embed-root-container';
const clickReceiverAttribute = 'data-lazy-load-embed-click-receiver';

// Local Functions
const generateLoadAttribute = (id) => `${loadAttribute}="${id}"`;

const getLazyLoadButtons = (element) => [...element.querySelectorAll(`button[${loadAttribute}]`)];

const createOriginalEmbedHTML = ({ tagName, attribs }) => {
  const tag = document.createElement(tagName);
  for (const [name, value] of Object.entries(attribs)) {
    try {
      // Wrapping this in a try/catch in case `name` is an invalid attribute
      tag.setAttribute(name, value);
    } catch {
      logger.warning(`LazyLoadEmbed invalid attribute ${name} with value ${value}`);
    }
  }
  return tag;
};

const getRootContainer = (element) => {
  if (element.getAttribute(rootContainerAttribute) !== null) {
    return element;
  }
  return getRootContainer(element.parentElement);
};

const makeReplaceWithOriginalEmbed = (rootContainer, originalEmbed) => () => {
  const parentContainer = rootContainer.parentNode;
  parentContainer.insertBefore(createOriginalEmbedHTML(originalEmbed), rootContainer);
  rootContainer.remove();
};

const setupLazyLoadEmbeds = (container, lazyLoadEmbeds, onEmbedStarted) => {
  const playButtons = getLazyLoadButtons(container);
  // eslint-disable-next-line unicorn/no-array-reduce -- Without a reduce this becomes much more convoluted to write
  const tearDown = playButtons.reduce(
    (tearDownFunction, playButton) => {
      // First we locate the elements that require interactivity
      const rootContainer = getRootContainer(playButton);
      const id = playButton.getAttribute(loadAttribute);
      const originalEmbed = lazyLoadEmbeds[id];
      const replaceWithOriginalEmbed = makeReplaceWithOriginalEmbed(rootContainer, originalEmbed);
      const clickReceiver = rootContainer.querySelector(`*[${clickReceiverAttribute}]`);
      const boundOnEmbedStarted = () => onEmbedStarted({ url: originalEmbed.attribs.src });
      // Then we add click listeners to replace them with the interactive embeds
      // and track events
      clickReceiver.addEventListener('click', replaceWithOriginalEmbed);
      clickReceiver.addEventListener('click', boundOnEmbedStarted);

      // Finally we return a combined tearDown function that includes all previous tear downs
      // and setup the ones for the current playButton
      return () => {
        tearDownFunction();
        if (clickReceiver) {
          clickReceiver.removeEventListener('click', replaceWithOriginalEmbed);
          clickReceiver.removeEventListener('click', boundOnEmbedStarted);
        }
      };
    },
    () => {},
  );
  return { tearDown };
};

// Module Exports
export {
  clickReceiverAttribute,
  generateLoadAttribute,
  rootContainerAttribute,
  setupLazyLoadEmbeds,
};
